import { Box, type SxProps, type Theme, useForkRef } from "@mui/material";
import { useGlobalStore } from "controller";
import { type ReactNode, type Ref, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import type { AcceptedDictation, DictationTarget } from "shared";
import { useEditorDictation } from "../hooks/useEditorDictation";
import { dictationIssueMessage } from "../utils/dictationMessages";
import { DictationMicButton } from "./DictationMicButton";
import { DictationPanel } from "./DictationPanel";
import { DictationView } from "./DictationView";

/** What an editor needs to take dictation — its three generic slots. */
export interface DictationSlots<Handle> {
  /** The editor's handle (`getEditor()`), read when ✓'s words go in. */
  ref: Ref<Handle>;
  /** The mic, for the editor's trailing action spot. Null when it can't dictate. */
  endAdornment: ReactNode;
  /** The dictation box, for the editor's cover. Null when not dictating. */
  overlay: ReactNode;
}

interface WithDictationProps<Handle> {
  /** The editor's handle as an insert target — `tiptapTarget` or `quillTarget`. */
  toTarget: (handle: Handle | null) => DictationTarget | null;
  /** Renders the editor with the slots, like react-hook-form's `Controller`. */
  render: (slots: DictationSlots<Handle>) => ReactNode;
  /** Read-only or saving: no mic and no push-to-talk. */
  disabled?: boolean;
  /** The caller's own ref to the editor (e.g. `field.ref`) — still attached. */
  editorRef?: Ref<Handle>;
  /** Size and placement of the mic — each editor's toolbar differs. */
  micSx?: SxProps<Theme>;
  /** The wrapper's layout, where the parent expects the editor to fill it. */
  sx?: SxProps<Theme>;
  /** Words that finish after the editor unmounted — see `useVoiceDictation`. */
  onLeave?: (accepted: AcceptedDictation) => void;
}

/**
 * Voice dictation for any rich-text editor that has the three slots. The
 * editor stays dumb: it only places `endAdornment` and covers itself with
 * `overlay`. Turning dictation off for a screen = rendering the editor alone;
 * for everyone = `useIsVoiceDictationEnabled`.
 * Design: .claude/projects/shared/voice-dictation-editors/DESIGN.md §3.7.
 */
export const WithDictation = <Handle,>({
  toTarget,
  render,
  disabled,
  editorRef,
  micSx,
  sx,
  onLeave,
}: WithDictationProps<Handle>) => {
  const timezone = useGlobalStore((state) => state.timezone);
  const handleRef = useRef<Handle | null>(null);
  const ref = useForkRef(handleRef, editorRef);
  const dictation = useEditorDictation({
    getTarget: () => toTarget(handleRef.current),
    disabled,
    onLeave,
  });
  const { issue, isDictating, dismissIssue } = dictation;

  // Dictation that couldn't start (or ended before any words) is back to the
  // editor, so the reason goes in a toast. Words kept show it in the box.
  useEffect(() => {
    if (!issue || isDictating) return;
    toast.error(dictationIssueMessage(issue, timezone));
    dismissIssue();
  }, [issue, isDictating, dismissIssue, timezone]);

  return (
    <Box ref={dictation.scopeRef} {...dictation.scopeProps} sx={sx}>
      {render({
        ref,
        endAdornment: dictation.canDictate ? (
          <DictationMicButton onClick={dictation.start} sx={micSx} />
        ) : null,
        overlay: isDictating ? (
          <DictationPanel>
            <DictationView {...dictation.viewProps} />
          </DictationPanel>
        ) : null,
      })}
    </Box>
  );
};
